<template>
  <div class="list-container">
    <div v-if="!settings.onDisabled" class="item-list">
      <div class="mb-8">
        <div class="md-title">{{ titlePrefix }}{{ settings.onText }}</div>
        <div v-if="settings.onSubtitle" class="md-caption">{{ settings.onSubtitle }}</div>
      </div>
      <md-content ref="onList" class="md-scrollbar gray-3 bg scroll-content" @scroll="updateScrollOn">
        <dialogue-list
          :settings="settings"
          :items="state.itemsOn"
          :drag-message="!settings.isParentChild ? state.dragMessageOn : null"
          :readonly="readonly"
          @startDrag="startDrag(true)"
          @dropItem="(item) => dropItem(item, false)"
          @editQuestion="(item, value) => editQuestion(item, value)"
          @editAnswer="(item, value) => editAnswer(item, value)"
          @playAudio="playAnswerText"
          @editEnabled="(item, value) => editEnabled(item, value, false)"
          @remove="removeOn"
        />
        <md-button
          v-if="showFabOn"
          class="md-fab md-fab-bottom-center md-dense md-primary"
          @click="scrollToBottomOn"
        >
          <md-icon>arrow_downward</md-icon>
        </md-button>
      </md-content>
    </div>
    <div class="item-list">
      <div class="mb-8">
        <div class="md-title">{{ titlePrefix }}{{ settings.offText }}</div>
        <div v-if="settings.offSubtitle" class="md-caption">{{ settings.offSubtitle }}&nbsp;</div>
      </div>
      <md-content ref="offList" class="md-scrollbar gray-3 bg scroll-content" @scroll="updateScrollOff">
        <dialogue-list
          :settings="settings"
          :items="state.itemsOff"
          :drag-message="!settings.isParentChild ? state.dragMessageOff : null"
          :readonly="readonly"
          @startDrag="startDrag(false)"
          @dropItem="(item) => dropItem(item, true)"
          @editQuestion="(item, value) => editQuestion(item, value)"
          @editAnswer="(item, value) => editAnswer(item, value)"
          @playAudio="playAnswerText"
          @editEnabled="(item, value) => editEnabled(item, value, true)"
          @remove="removeOff"
        />
        <md-button
          v-if="showFabOff"
          class="md-fab md-fab-bottom-center md-dense md-primary"
          @click="scrollToBottomOff"
        >
          <md-icon>arrow_downward</md-icon>
        </md-button>
      </md-content>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { debounce, sortBy, uniqBy, groupBy, cloneDeep, omit } from 'lodash';
import Chance from 'chance';

import DialogueList from './DialogueList';

export default {
  name: 'dialogue-editor',
  components: {
    DialogueList,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    titlePrefix: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      scrollSettings: {
        onScroll: {
          isVisible: false,
          hasScrolled: false
        },
        offScroll: {
          isVisible: false,
          hasScrolled: false
        }
      },
      state: {
        itemsOn: [],
        itemsOff: [],
        dragMessageOn: null,
        dragMessageOff: null,
      },
    };
  },
  computed: {
    showFabOn() {
      return this.scrollSettings.onScroll.isVisible && !this.scrollSettings.onScroll.hasScrolled;
    },
    showFabOff() {
      return this.scrollSettings.offScroll.isVisible && !this.scrollSettings.offScroll.hasScrolled;
    },
    dialogueWarnings() {
      const warnings = this.$store.getters['validation/visibleWarningsByField'].dialogue || [];
      return uniqBy(warnings, 'message');
    },
    dialogueWarningsById() {
      return groupBy(this.dialogueWarnings, 'metadata.componentId');
    },
  },
  created() {
    this.makeLocalCopy();
    this.emitUpdate = debounce(this.emitUpdate, 2000);
  },
  mounted() {
    if (this.settings.type === 'Q_AND_A') {
      this.$eventHub.$on('DIALOGUE::ADD', items => {
        this.addQuestionList(items)
      });
    }
    window.addEventListener('resize', this.updateScrollbarVis);
    this.updateScrollbarVis();
  },
  destroyed() {
    this.$eventHub.$off('DIALOGUE::ADD');
    window.removeEventListener('resize', this.updateScrollbarVis);
  },
  methods: {
    addQuestionList(items) {
      for (const item of items) {
        const insertIndex = this.state.itemsOn.length - 1;
        this.state.itemsOn.splice(insertIndex, 0, this.createItem(item.content.en, true, insertIndex));
      }
      this.emitUpdate();
    },
    makeLocalCopy() {
      if (this.settings.onDisabled) {
        this.state.itemsOn = [];
      } else {
        this.state.itemsOn = sortBy(
          cloneDeep(this.items).filter(item => item.options.enabled),
          'options.position'
        );
      }

      this.state.itemsOff = sortBy(
        cloneDeep(this.items).filter(item => !item.options.enabled),
        'options.position'
      );

      this.updateDummyItems();
      this.updatePositions();
    },
    findParent(item, isOff) {
      let parentList;
      if (isOff) {
        parentList = this.state.itemsOff;
      } else {
        parentList = this.state.itemsOn;
      }
      return parentList.find(parent => parent.children && parent.children.find(child => child.id === item.id));
    },
    editQuestion(item, value) {
      item.content.en = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    editAnswer(item, value) {
      item.options.answer.en = value;
      this.convertNewItem(item);
      this.updateDummyItems();
      this.updatePositions();
      this.emitUpdate();
    },
    convertNewItem(item) {
      if (item.isNew) {
        this.$analytics.event('Authoring Tool', 'question_add_question'); // tbd
        item.isNew = false;
      }
    },
    updatePositions() {
      let parentIndex = 0;
      for (const parent of this.state.itemsOn) {
        delete parent.parent;
        parent.options.position = parentIndex++;
        let childIndex = 0;
        for (const child of parent.children || []) {
          child.parent = parent;
          child.options.position = childIndex++;
        }
      }
      for (const parent of this.state.itemsOff) {
        delete parent.parent;
        parent.options.position = parentIndex++;
        let childIndex = 0;
        for (const child of parent.children || []) {
          child.parent = parent;
          child.options.position = childIndex++;
        }
      }
    },
    needsDummyItem(list) {
      if (!list || list.length === 0) {
        return true;
      }
      if (!list[list.length - 1].isNew) {
        return true;
      }
      return false;
    },
    updateDummyItems() {
      if (this.needsDummyItem(this.state.itemsOn)) {
        this.appendNewItem(false);
      }
      if (this.settings.isParentChild) {
        for (const item of this.state.itemsOn) {
          if (this.needsDummyItem(item.children)) {
            this.appendNewChild(item, false);
          }
        }
      }

      if (this.needsDummyItem(this.state.itemsOff)) {
        this.appendNewItem(true);
      }
      if (this.settings.isParentChild) {
        for (const item of this.state.itemsOff) {
          if (this.needsDummyItem(item.children)) {
            this.appendNewChild(item, true);
          }
        }
      }
    },
    editEnabled(item, value) {
      item.options.enabled = value;
      this.emitUpdate();
    },
    appendNewItem(isOff) {
      const newItem = this.createItem('', !isOff, undefined, true);
      if (isOff) {
        this.state.itemsOff.push(newItem);
      } else {
        this.state.itemsOn.push(newItem);
      }
      return newItem;
    },
    appendNewChild(parentItem, isOff) {
      if (!parentItem.children) {
        Vue.set(parentItem, 'children', []);
      }
      const childItem = this.createItem('', !isOff, parentItem.children.length, true);
      childItem.parent = parentItem;
      parentItem.children.push(childItem);
    },
    playAnswerText(item) {
      this.$emit('playAudio', item)
    },
    removeOn(item) {
      const parentItem = this.findParent(item, false);
      if (parentItem) {
        const childIndex = parentItem.children.findIndex(child => child.id === item.id);
        if (childIndex >= 0) {
          parentItem.children.splice(childIndex, 1);
          this.emitUpdate();
        }
      } else {
        const index = this.state.itemsOn.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.state.itemsOn.splice(index, 1);
          this.emitUpdate();
        }
      }
    },
    removeOff(item) {
      const parentItem = this.findParent(item, true);
      if (parentItem) {
        const childIndex = parentItem.children.findIndex(child => child.id === item.id);
        if (childIndex >= 0) {
          parentItem.children.splice(childIndex, 1);
          this.emitUpdate();
        }
      } else {
        const index = this.state.itemsOff.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.state.itemsOff.splice(index, 1);
          this.emitUpdate();
        }
      }
    },
    startDrag(isOnList) {
      if (isOnList) {
        this.state.dragMessageOn = 'Drag and drop here to reorder';
        this.state.dragMessageOff = 'Drag and drop here to turn off';
      } else {
        this.state.dragMessageOn = 'Drag and drop here to turn on';
        this.state.dragMessageOff = 'Drag and drop here to reorder';
      }
    },
    // eslint-disable-next-line no-unused-vars
    dropItem(item, isOff) {
      this.updateDummyItems();
      this.updatePositions();
      this.state.dragMessageOn = null;
      this.state.dragMessageOff = null;
      // Item has already been moved, update enabled status
      if (!this.settings.onDisabled) {
        for (const item of this.state.itemsOn) {
          item.options.enabled = true;
        }
        for (const item of this.state.itemsOff) {
          item.options.enabled = false;
        }
      }
      this.emitUpdate();
    },
    createItem(content, enabled, position, isNew = undefined) {
      const chance = new Chance();
      return {
        id: chance.guid(),
        isNew: isNew,
        content: this.settings.isQuestionDisabled ? undefined : { en: content },
        options: {
          enabled: this.settings.onDisabled ? undefined : enabled,
          answer: this.settings.isAnswerDisabled? undefined :  { en: '' },
          position,
        },
      };
    },
    emitUpdate() {
      const updatedItems = this.state.itemsOn
        .concat(this.state.itemsOff)
        .filter(item => !item.isNew)
        .map(item => {
          return {
            ...omit(item, ['parent', 'isNew']),
            children: item.children
              ? item.children.filter(child => !child.isNew).map(child => omit(child, ['parent', 'isNew']))
              : undefined
          };
        });
      this.$emit('update', updatedItems);
    },
    updateScrollbarVis() {
      this.scrollSettings.onScroll.isVisible = !!(this.$refs.onList && this.$refs.onList.$el.scrollHeight > this.$refs.onList.$el.clientHeight);
      this.scrollSettings.offScroll.isVisible = !!(this.$refs.offList && this.$refs.offList.$el.scrollHeight > this.$refs.offList.$el.clientHeight);
    },
    updateScrollOn() {
      this.scrollSettings.onScroll.hasScrolled = true;
    },
    scrollToBottomOn() {
      if (this.$refs.onList) {
        this.$refs.onList.$el.scrollTo({
          top: this.$refs.onList.$el.scrollHeight,
          left: 0,
          behavior: 'smooth'
        })
        this.scrollSettings.onScroll.hasScrolled = true;
      }
    },
    updateScrollOff() {
      this.scrollSettings.offScroll.hasScrolled = true;
    },
    scrollToBottomOff() {
      if (this.$refs.offList) {
        this.$refs.offList.$el.scrollTo({
          top: this.$refs.offList.$el.scrollHeight,
          left: 0,
          behavior: 'smooth'
        })
        this.scrollSettings.offScroll.hasScrolled = true;
      }
    },
  }
}
</script>

<style scoped>
.md-field:first-child {
  margin-right: 20px;
}

.list-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: calc(100% - 64px);
}

.mb-8 {
  margin-bottom: 8px;
}

.item-list {
  width: 100%;
  max-width: 50%;
  display: table;
  height: 100%;
}

.item-list:not(:last-child) {
  margin-right: 15px;
  padding-right: 15px;
  border-right: 1px solid #9f9f9f;
}

.scroll-content {
  position: relative;
  overflow-y: auto;
  height: 100%;
}
</style>